import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Url } from '@rossum/api-client';
import { ConfirmationDialog } from '@rossum/components/confirmation-dialog/ConfirmationDialog';
import { Stack, Typography } from '@rossum/ui/material';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { QueueSelectList } from '../../queues/select-queue/QueueSelectList';

export const ConnectQueueDialog = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  trainingQueueUrls,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (queueUrls: Url[]) => void;
  isLoading: boolean;
  trainingQueueUrls: Url[];
}) => {
  const intl = useIntl();
  const [selectedQueues, setSelectedQueues] = useState<number[]>([]);
  const { workspacesWithQueues, isLoading: isFetchingQueues } =
    useWorkspacesWithQueues({
      enableQueries: open,
    });

  const filteredQueues = useMemo(() => {
    if (!workspacesWithQueues) return [];
    const trainingQueueUrlSet = new Set(trainingQueueUrls);
    return workspacesWithQueues.map(ws => {
      const availableQueues = ws.queues.filter(
        q => !trainingQueueUrlSet.has(q.url)
      );
      return { ...ws, queues: availableQueues };
    });
  }, [workspacesWithQueues, trainingQueueUrls]);

  const queueUrlMap = useMemo<Map<number, string>>(() => {
    const allQueues = filteredQueues.map(ws => ws.queues).flat();

    return new Map(allQueues.map(q => [q.id, q.url]));
  }, [filteredQueues]);

  const handleClose = () => {
    setSelectedQueues([]);
    onClose();
  };

  const handleConfirm = () => {
    const queueUrls = selectedQueues.flatMap(id => queueUrlMap.get(id) || []);
    onConfirm(queueUrls);
    handleClose();
  };

  return (
    <ConfirmationDialog
      open={open}
      onCancel={handleClose}
      onConfirm={handleConfirm}
      confirmActionText={intl.formatMessage({ id: 'common.add' })}
      title={intl.formatMessage({
        id: 'features.engines.engineDetail.connectedQueues.dialogTitle',
      })}
      description={
        <Stack spacing={0.5} useFlexGap>
          <Typography variant="body2">
            {intl.formatMessage({
              id: 'features.engines.engineDetail.connectedQueues.dialogDescription',
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {intl.formatMessage({
              id: 'features.engines.engineDetail.connectedQueues.dialogWarning',
            })}
          </Typography>
        </Stack>
      }
      isLoading={isLoading}
      disabled={selectedQueues.length === 0}
    >
      <QueueSelectList
        queuesByWorkspace={filteredQueues}
        isLoading={isFetchingQueues}
        selectedQueues={selectedQueues}
        onSelect={ids => setSelectedQueues(ids)}
        disabled={isLoading}
      />
    </ConfirmationDialog>
  );
};
